/**
 * Sunice Swatch Carousel (native scroll-snap)
 * Replaces Swiper-based color swatch sliders on product/variant cards.
 *
 * Markup expected (already used by cards):
 * .color-swiper-container
 *   .color-swiper-button-prev
 *   .color-swiper-wrapper
 *     .color-swiper-slide
 *   .color-swiper-button-next
 *
 * This module:
 * - Enables horizontal scroll + snap on the wrapper
 * - Shows arrows only when wrapper overflows
 * - Supports many instances + dynamic content
 */

(() => {
  const W = /** @type {any} */ (window);

  // Guard against redefinition in Theme Editor reinjections
  if (W.SuniceSwatchCarousel) return;

  /** @type {WeakMap<HTMLElement, { hasMoved: boolean, reachedEnd: boolean, ro?: ResizeObserver, mo?: MutationObserver, updateRaf?: number, needsGeometryCheck?: boolean }>} */
  const state = new WeakMap();

  const GAP = 12; // keep parity with existing card spacing
  const EDGE_EPS = 6; // px tolerance for "at start/end" (avoids 1-5px rounding showing prev on load)

  /**
   * @param {HTMLElement} container
   */
  function getEls(container) {
    const wrapper = container.querySelector('.color-swiper-wrapper');
    const prev = container.querySelector('.color-swiper-button-prev');
    const next = container.querySelector('.color-swiper-button-next');
    return {
      wrapper: wrapper instanceof HTMLElement ? wrapper : null,
      prev: prev instanceof HTMLElement ? prev : null,
      next: next instanceof HTMLElement ? next : null,
    };
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function computeStep(wrapper) {
    // Avoid getBoundingClientRect - use offsetWidth instead (cheaper, no forced reflow)
    const wrapperWidth = wrapper.clientWidth;
    
    // Prefer first slide width for natural scrolling
    const first = wrapper.querySelector('.color-swiper-slide');
    if (first instanceof HTMLElement) {
      // Use offsetWidth instead of getBoundingClientRect().width
      // offsetWidth is cheaper and doesn't force a reflow in most cases
      const w = first.offsetWidth;
      if (w > 0) return Math.round(w + GAP);
    }
    // Fallback: scroll by ~80% of viewport width (use already-read value)
    return Math.round(wrapperWidth * 0.8);
  }

  /**
   * Internal update function that performs the actual DOM updates
   * @param {HTMLElement} container
   */
  function updateInternal(container) {
    const { wrapper, prev, next } = getEls(container);
    if (!wrapper) return;
    
    // BATCH ALL STYLE WRITES FIRST to avoid forced reflows
    // Write all container styles
    container.style.width = '100%';
    container.style.maxWidth = '100%';
    container.style.minWidth = '0';

    // Write all wrapper styles
    wrapper.style.overflowX = 'auto';
    wrapper.style.scrollSnapType = 'x mandatory';
    wrapper.style.scrollBehavior = 'smooth';
    wrapper.style.display = 'flex';
    wrapper.style.gap = `${GAP}px`;
    wrapper.style.flexWrap = 'nowrap';
    wrapper.style.width = '100%';
    wrapper.style.maxWidth = '100%';
    wrapper.style.minWidth = '0';

    // Write all slide styles
    wrapper.querySelectorAll('.color-swiper-slide').forEach((slide) => {
      if (slide instanceof HTMLElement) {
        slide.style.scrollSnapAlign = 'start';
        slide.style.flex = '0 0 auto';
      }
    });

    // NOW batch all DOM reads together after all writes are complete
    // This prevents forced reflows by reading all geometric properties in one batch
    const scrollWidth = wrapper.scrollWidth;
    const clientWidth = wrapper.clientWidth;
    const scrollLeft = wrapper.scrollLeft;
    const isOverflowing = scrollWidth > clientWidth + 5; // Increased tolerance
    // Only toggle classes if there's a real change to avoid unnecessary updates
    if (isOverflowing) {
      container.classList.add('sunice-swatch-carousel--scrollable');
      container.classList.remove('sunice-swatch-carousel--static');
    } else {
      container.classList.remove('sunice-swatch-carousel--scrollable');
      container.classList.add('sunice-swatch-carousel--static');
    }

    // Position-based behavior:
    // Detect "start/end" using scroll properties only (avoid getBoundingClientRect to prevent reflows)
    // Use scrollLeft and scrollWidth which are cheaper to read and don't force reflows
    const wrapperClientWidth = clientWidth;
    const wrapperScrollWidth = scrollWidth;

    // Use scroll-based detection only - this avoids expensive getBoundingClientRect calls
    // scrollLeft is accurate for determining scroll position without forcing reflows
    const atStartByScroll = scrollLeft <= EDGE_EPS;
    const atEndByScroll = scrollLeft + wrapperClientWidth >= wrapperScrollWidth - EDGE_EPS;

    // Use scroll-based detection as primary method (avoids forced reflows)
    // Only fall back to geometry checks if absolutely necessary (rare edge cases)
    let atStartNow = atStartByScroll;
    let atEndNow = atEndByScroll;
    
    // CRITICAL: If we just determined overflow exists, we can't be at end initially
    // unless scroll position clearly indicates it
    if (isOverflowing && scrollLeft <= EDGE_EPS && !atEndByScroll) {
      atEndNow = false; // Force false if we're at start and there's overflow
    }
    
    // Only use expensive getBoundingClientRect as absolute last resort for edge cases
    // This should rarely be needed since scrollLeft is generally reliable
    // Cache the state to avoid repeated checks
    const containerState = state.get(container);
    const needsGeometryCheck = containerState?.needsGeometryCheck !== false;
    
    if (needsGeometryCheck && !atStartByScroll && !atEndByScroll) {
      // Only do expensive check if scroll detection is ambiguous
      // This happens rarely, so the reflow cost is acceptable
      const slides = wrapper.querySelectorAll('.color-swiper-slide');
      const first = slides[0];
      const last = slides[slides.length - 1];
      
      if (first instanceof HTMLElement && last instanceof HTMLElement) {
        // Batch all getBoundingClientRect calls together (only if needed)
        const wrapRect = wrapper.getBoundingClientRect();
        const firstRect = first.getBoundingClientRect();
        const lastRect = last.getBoundingClientRect();
        
        // Read computed style only when needed (causes reflow but only in edge cases)
        const cs = window.getComputedStyle(wrapper);
        const padLeft = Number.parseFloat(cs.paddingLeft || '0') || 0;
        const padRight = Number.parseFloat(cs.paddingRight || '0') || 0;

        // "Start" means the first swatch aligns with the left padding edge.
        const startDelta = firstRect.left - wrapRect.left - padLeft;
        // "End" means the last swatch aligns with the right padding edge.
        const endDelta = wrapRect.right - lastRect.right - padRight;
        
        // Use geometry check only if it differs significantly from scroll check
        if (Math.abs(startDelta) > EDGE_EPS * 2) {
          atStartNow = Math.abs(startDelta) <= EDGE_EPS;
        }
        if (Math.abs(endDelta) > EDGE_EPS * 2) {
          atEndNow = Math.abs(endDelta) <= EDGE_EPS;
        }
        
        // Cache that geometry check worked, so we can skip it next time
        if (containerState) {
          containerState.needsGeometryCheck = false;
        }
      }
    }

    // Check if scrollable class is present - this is the source of truth
    const isScrollable = container.classList.contains('sunice-swatch-carousel--scrollable');
    
    // SIMPLE LOGIC: If scrollable class is present, show arrows based on position
    // If NOT scrollable, hide both arrows always
    if (!isScrollable) {
      // Not scrollable - hide both arrows always
      if (prev) {
        prev.classList.add('is-hidden');
        prev.style.display = 'none';
        prev.setAttribute('aria-hidden', 'true');
        prev.setAttribute('tabindex', '-1');
      }
      if (next) {
        next.classList.add('is-hidden');
        next.style.display = 'none';
        next.setAttribute('aria-hidden', 'true');
        next.setAttribute('tabindex', '-1');
      }
      return; // Exit early - nothing more to do
    }
    
    // SCROLLABLE: Determine which arrows to show based on position only
    const shouldShowPrev = !atStartNow;
    const shouldShowNext = !atEndNow;
    
    if (prev) {
      if (shouldShowPrev) {
        prev.classList.remove('is-hidden');
        prev.style.display = 'flex';
        prev.style.visibility = 'visible';
        prev.style.opacity = '1';
        prev.style.pointerEvents = 'auto';
        prev.setAttribute('aria-hidden', 'false');
        prev.setAttribute('tabindex', '0');
      } else {
        prev.classList.add('is-hidden');
        prev.style.display = 'none';
        prev.setAttribute('aria-hidden', 'true');
        prev.setAttribute('tabindex', '-1');
      }
    }
    
    if (next) {
      if (shouldShowNext) {
        next.classList.remove('is-hidden');
        next.style.display = 'flex';
        next.style.visibility = 'visible';
        next.style.opacity = '1';
        next.style.pointerEvents = 'auto';
        next.setAttribute('aria-hidden', 'false');
        next.setAttribute('tabindex', '0');
      } else {
        next.classList.add('is-hidden');
        next.style.display = 'none';
        next.setAttribute('aria-hidden', 'true');
        next.setAttribute('tabindex', '-1');
      }
    }
    
  }

  /**
   * Public update function with throttling via requestAnimationFrame
   * @param {HTMLElement} container
   */
  function update(container) {
    // Throttle updates using requestAnimationFrame to batch with browser's layout cycle
    const containerState = state.get(container);
    if (containerState?.updateRaf) {
      return; // Update already scheduled
    }
    
    const rafId = requestAnimationFrame(() => {
      const currentState = state.get(container);
      if (currentState?.updateRaf === rafId) {
        currentState.updateRaf = undefined;
        updateInternal(container); // Perform actual update
      }
    });
    
    if (containerState) {
      containerState.updateRaf = rafId;
    } else {
      state.set(container, { hasMoved: false, reachedEnd: false, updateRaf: rafId, needsGeometryCheck: true });
    }
  }

  /**
   * @param {HTMLElement} container
   */
  function attach(container) {
    // Initialize state if missing
    if (!state.has(container)) {
      state.set(container, { 
        hasMoved: false, 
        reachedEnd: false,
        needsGeometryCheck: true // Start with geometry check enabled, will disable after first successful check
      });
    } else {
      updateInternal(container);
      return;
    }

    const { wrapper, prev, next } = getEls(container);
    if (!wrapper) return;

    // Safe default: hide arrows until we measure overflow + scroll position
    if (prev) prev.classList.add('is-hidden');
    if (next) next.classList.add('is-hidden');

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateInternal(container);
      });
    };

    const onPrev = (e) => {
      e.preventDefault();
      const step = computeStep(wrapper);
      wrapper.scrollBy({ left: -step, behavior: 'smooth' });
    };

    const onNext = (e) => {
      e.preventDefault();
      const step = computeStep(wrapper);
      wrapper.scrollBy({ left: step, behavior: 'smooth' });
    };

    if (prev) {
      prev.setAttribute('role', 'button');
      prev.setAttribute('tabindex', '0');
      prev.addEventListener('click', onPrev);
    }
    if (next) {
      next.setAttribute('role', 'button');
      next.setAttribute('tabindex', '0');
      next.addEventListener('click', onNext);
    }

    wrapper.addEventListener('scroll', onScroll, { passive: true });

    // Throttle ResizeObserver updates to reduce forced reflows
    // Use a debounce mechanism to prevent excessive updates
    let resizeTimeout = 0;
    const ro = new ResizeObserver(() => {
      // Clear any pending update
      if (resizeTimeout) {
        cancelAnimationFrame(resizeTimeout);
      }
      // Debounce resize updates - only update after resize settles
      resizeTimeout = requestAnimationFrame(() => {
        resizeTimeout = 0;
        updateInternal(container);
      });
    });
    ro.observe(wrapper);

    // Throttle MutationObserver updates to reduce forced reflows
    // Use a debounce mechanism to prevent excessive updates
    let mutationTimeout = 0;
    const mo = new MutationObserver(() => {
      // Clear any pending update
      if (mutationTimeout) {
        cancelAnimationFrame(mutationTimeout);
      }
      // Debounce mutation updates - only update after mutations settle
      mutationTimeout = requestAnimationFrame(() => {
        mutationTimeout = 0;
        updateInternal(container);
      });
    });
    mo.observe(wrapper, { childList: true, subtree: true });

    const current = state.get(container) || { hasMoved: false, reachedEnd: false, needsGeometryCheck: true };
    state.set(container, { ...current, ro, mo, needsGeometryCheck: current.needsGeometryCheck !== false });
    
    // Defer initial updates to avoid forced reflows during page load
    // Use requestAnimationFrame to batch with browser's layout cycle
    // Only do ONE initial update, then let ResizeObserver handle subsequent changes
    requestAnimationFrame(() => {
      updateInternal(container);
      // Single delayed check for late-loading images (fonts/images can change scrollWidth)
      // Don't do multiple updates - let observers handle dynamic changes
      setTimeout(() => {
        // Only update if container is still in DOM and visible
        if (container.isConnected && container.offsetParent !== null) {
          updateInternal(container);
        }
      }, 200);
    });
  }

  /**
   * @param {ParentNode} root
   */
  function init(root = document) {
    const containers = root.querySelectorAll('.color-swiper-container');
    containers.forEach((c) => {
      if (c instanceof HTMLElement) attach(c);
    });
  }

  // Expose API for other scripts that load cards dynamically
  W.SuniceSwatchCarousel = { init, update: (el) => el instanceof HTMLElement && update(el) };

  // Defer initialization until idle to reduce main thread blocking
  // Only initialize visible containers immediately, defer others
  function initDeferred() {
    // Use requestIdleCallback for non-critical initialization
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => init(document), { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => init(document), 100);
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeferred, { once: true });
  } else {
    initDeferred();
  }

  // Auto-init on Shopify section loads (Theme Editor)
  document.addEventListener('shopify:section:load', (e) => {
    const target = /** @type {any} */ (e).target;
    if (target && target.querySelectorAll) init(target);
  });

  // Light global observer for dynamically injected cards (debounced)
  let _swatchObserverTimeout = null;
  const bodyObserver = new MutationObserver((mutations) => {
    let hasNew = false;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof HTMLElement)) continue;
        if (n.matches?.('.color-swiper-container') || (n.querySelectorAll && n.querySelector('.color-swiper-container'))) {
          hasNew = true;
          break;
        }
      }
      if (hasNew) break;
    }
    if (hasNew) {
      if (_swatchObserverTimeout) clearTimeout(_swatchObserverTimeout);
      _swatchObserverTimeout = setTimeout(() => {
        _swatchObserverTimeout = null;
        init(document);
      }, 300);
    }
  });
  bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
